import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.1 + i * 0.09, duration: 0.7, ease: [0.22, 1, 0.36, 1] } }),
};

const GOBAN = "https://images.unsplash.com/photo-1633974026122-2861ae7b6087?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

export default function Hero({ author, taxonomy }) {
  const stats = [
    { label: "Published Briefs", value: `${taxonomy?.total ?? "—"}` },
    { label: "Core Themes", value: `${taxonomy?.theme_count ?? 6}` },
    { label: "Outlets", value: `${taxonomy?.source_count ?? 5}` },
    { label: "Span", value: "2024–26" },
  ];

  return (
    <section className="relative pt-36 sm:pt-44 pb-16 sm:pb-20 overflow-hidden">
      {/* Goban (Go board) backdrop — strategy, patience, position */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-y-0 right-0 w-full sm:w-[62%]"
          style={{
            backgroundImage: `url(${GOBAN})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.55,
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, var(--ink) 34%, rgba(var(--ink-rgb),0.72) 58%, rgba(var(--ink-rgb),0.42) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(0deg, var(--ink) 2%, transparent 42%)" }}
        />
        <div
          className="absolute -top-40 right-10 w-[460px] h-[460px] rounded-full"
          style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--gold) 14%, transparent), transparent 65%)" }}
        />
      </div>
      <div className="relative max-w-[1320px] mx-auto px-5 sm:px-8">
        <motion.p custom={0} variants={fade} initial="hidden" animate="show"
          className="meta-label text-[var(--gold)] flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-[var(--gold)] live-dot" />
          Classified Briefs &amp; Essays · Dossier Updated
        </motion.p>

        <motion.h1 custom={1} variants={fade} initial="hidden" animate="show"
          className="font-serif text-[3.4rem] leading-[0.95] sm:text-7xl lg:text-8xl tracking-tight mt-6 text-[var(--txt)]">
          Dre Lapiello
        </motion.h1>

        <motion.p custom={2} variants={fade} initial="hidden" animate="show"
          className="mt-6 max-w-2xl font-serif italic text-xl sm:text-2xl text-[var(--txt-2)] leading-snug">
          {author?.tagline || "7 min perspectives on moral psychology, comparative mythology and geopolitics."}
        </motion.p>

        <motion.p custom={3} variants={fade} initial="hidden" animate="show"
          className="mt-4 max-w-xl text-sm sm:text-[0.95rem] text-[var(--muted)] leading-relaxed">
          {author?.title}. An archive of my written work — gathered from Substack, Medium and the
          press, organized by theme so you can read your way in.
        </motion.p>

        <motion.div custom={4} variants={fade} initial="hidden" animate="show"
          className="mt-12 grid grid-cols-2 sm:grid-cols-4 border-t border-l border-[var(--line)] max-w-3xl">
          {stats.map((s) => (
            <div key={s.label} className="border-b border-r border-[var(--line)] px-5 py-5">
              <div className="font-serif text-3xl sm:text-4xl text-[var(--gold)]">{s.value}</div>
              <div className="meta-label text-[var(--muted)] mt-2 text-[0.6rem]">{s.label}</div>
            </div>
          ))}
        </motion.div>

        <motion.div custom={5} variants={fade} initial="hidden" animate="show" className="mt-12 flex items-center gap-2 text-[var(--muted)]">
          <ArrowDown size={15} className="animate-bounce" />
          <span className="meta-label text-[0.6rem]">Scroll to the index</span>
        </motion.div>
      </div>
    </section>
  );
}
